import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { FC } from "react";

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (p: number) => void;
  isFetching: boolean;
}

export const Pagination: FC<PaginationProps> = ({
  page, totalPages, total, limit, onPageChange, isFetching,
}:any) => {
  const from = Math.min((page - 1) * limit + 1, total);
  const to   = Math.min(page * limit, total);

  const btn = (onClick: () => void, disabled: boolean, children: React.ReactNode) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className="p-2 rounded-lg border border-brand-border text-brand-muted
                 hover:text-white hover:border-brand-orange transition-colors
                 disabled:opacity-30 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );

  // Build page window (max 5 pages visible)
  const window = 2;
  const pages: number[] = [];
  for (
    let i = Math.max(1, page - window);
    i <= Math.min(totalPages, page + window);
    i++
  ) pages.push(i);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-1">
      <p className="text-sm text-brand-muted">
        Showing <span className="text-white font-medium">{from}–{to}</span> of{" "}
        <span className="text-white font-medium">{total}</span> applicants
        {isFetching && (
          <span className="ml-2 text-brand-orange text-xs animate-pulse">
            Loading…
          </span>
        )}
      </p>

      <div className="flex items-center gap-1">
        {btn(() => onPageChange(1),    page === 1,          <ChevronsLeft  size={16} />)}
        {btn(() => onPageChange(page - 1), page === 1,      <ChevronLeft   size={16} />)}

        {pages[0] > 1 && (
          <>
            <PageBtn p={1}           current={page} onClick={onPageChange} />
            {pages[0] > 2 && <span className="text-brand-muted px-1">…</span>}
          </>
        )}

        {pages.map((p) => (
          <PageBtn key={p} p={p} current={page} onClick={onPageChange} />
        ))}

        {pages[pages.length - 1] < totalPages && (
          <>
            {pages[pages.length - 1] < totalPages - 1 && (
              <span className="text-brand-muted px-1">…</span>
            )}
            <PageBtn p={totalPages}  current={page} onClick={onPageChange} />
          </>
        )}

        {btn(() => onPageChange(page + 1), page === totalPages, <ChevronRight  size={16} />)}
        {btn(() => onPageChange(totalPages), page === totalPages, <ChevronsRight size={16} />)}
      </div>
    </div>
  );
};

const PageBtn: FC<{ p: number; current: number; onClick: (p: number) => void }> = ({
  p, current, onClick,
}) => (
  <button
    onClick={() => onClick(p)}
    className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors
      ${p === current
        ? "bg-brand-green text-gray-900"
        : "text-brand-muted hover:text-white hover:bg-brand-border border border-brand-border"
      }`}
  >
    {p}
  </button>
);